import { toLeafText, type MeasureLabel } from '../standard/toLeafText';
import { halfChord } from '../svgLabels';
import { wrapWords } from '../wrapWords';
import { rankLabelLines } from '../rankLabelLines';
import { legibleLines } from '../legibleLines';

/** How many lines a leaf's label may take. More lines hold more name but each is narrower. */
export const LEAF_LINE_COUNTS = [1, 2, 3];

const roomiest = (candidates: string[][]): string[] =>
  candidates.reduce(
    (best, candidate) =>
      candidate.join(' ').length > best.join(' ').length ? candidate : best,
    [] as string[]
  );

type ToCircleLinesArgs = {
  label: string | null;
  /** Already formatted and parenthesised, e.g. `(4.7M)`. */
  valueLabel: string;
  r: number;
  lineCounts: number[];
  /** How far above the circle's center the text block sits. */
  centerOffset: number;
  measure: MeasureLabel;
  lineHeight: number;
};

/**
 * The lines that say the most about a circle, in the order a reader needs them: name and value,
 * then the name alone, then the value alone.
 *
 * A circle's usable width depends on how tall the text is: one line through the middle has
 * nearly the whole diameter, three lines only the chord out where the outermost of them sits. So
 * each line count is laid out against the chord it would actually have and the roomiest result
 * wins.
 *
 * The name outranks the value here, unlike in a treemap tile. A circle's area already carries
 * the value, and `toLeafText` reserves the number first — which on a circle too narrow to hold
 * both leaves the number wrapped over three lines with the name dropped entirely, labelling a
 * shape with a figure and no subject.
 */
export const toCircleLines = ({
  label,
  valueLabel,
  r,
  lineCounts,
  centerOffset,
  measure,
  lineHeight,
}: ToCircleLinesArgs): string[] => {
  /* Measured at the middle of the outermost line rather than its outer edge: what pokes past
     the circle there is the corner of a glyph rather than a whole letter, and measuring the
     edge costs a group its whole name, its band sitting flush against the circle by design. */
  const widthForLines = (lineCount: number) =>
    2 *
    halfChord(r, Math.abs(centerOffset) + ((lineCount - 1) * lineHeight) / 2);

  const perLineCount = (
    toLines: (availableWidth: number, lineCount: number) => string[]
  ) =>
    roomiest(
      lineCounts
        .map(lineCount => ({
          availableWidth: widthForLines(lineCount),
          lineCount,
        }))
        /* A circle with no width says nothing. Packing does not lay out until its container
           has a size, so there is no unmeasured circle here for `toLeafText` to answer whole. */
        .filter(({ availableWidth }) => availableWidth > 0)
        .map(({ availableWidth, lineCount }) =>
          toLines(availableWidth, lineCount)
        )
    );

  const withValue = perLineCount((availableWidth, lineCount) =>
    toLeafText({
      label: label ?? '',
      valueLabel,
      availableWidth,
      availableHeight: lineCount * lineHeight,
      measure,
      lineHeight,
      wrap: wrapWords,
    })
  );
  const nameOnly = label
    ? perLineCount((availableWidth, lineCount) =>
        legibleLines(
          wrapWords({
            text: label,
            availableWidth,
            measure,
            maxLines: lineCount,
          })
        )
      )
    : [];

  return rankLabelLines({ label, valueLabel, withValue, nameOnly });
};
